import { useState, useEffect, useCallback } from 'react';

type ThemePreference = 'light' | 'dark' | null;
type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'theme-preference';

function getSystemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference ?? getSystemTheme();
}

function applyTheme(theme: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme === 'dark' ? '#050508' : '#f3f4f6');
  }
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return null;
  });

  const resolved = resolveTheme(preference);

  // Apply theme on mount and when preference changes
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Listen for system preference changes (only when following system)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (preference === null) {
        applyTheme(getSystemTheme());
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [preference]);

  const setTheme = useCallback((p: ThemePreference) => {
    setPreference(p);
    if (p === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, p);
    }
  }, []);

  // Cycle: light → dark → system → light
  const toggle = useCallback(() => {
    if (preference === 'light') {
      setTheme('dark');
    } else if (preference === 'dark') {
      setTheme(null);
    } else {
      setTheme('light');
    }
  }, [preference, setTheme]);

  return {
    preference,
    resolved,
    isSystem: preference === null,
    toggle,
    setTheme,
  };
}
